import type { User, TokenResponse, AuthResponse, LoginRequest, RegisterRequest, Organization, OrgMembership, Team, TeamMembership, OrgInvite, CreateOrgRequest, UpdateOrgRequest, AddMemberRequest, UpdateMemberRoleRequest, CreateInviteRequest, CreateTeamRequest, UpdateTeamRequest, Plan, SubscriptionResponse, Invoice, PaymentMethod, CheckoutSession, BillingPortalSession, CreateSubscriptionRequest, CancelSubscriptionRequest, UsageSummary, BillingExemption, APIKey, APIKeyScope, CreateAPIKeyRequest, CreateAPIKeyResponse, OAuthProvider, AuditLog, AuditLogFilter, AuditEventType, StorageConfig, StorageConfigRequest, StorageTestResult, StorageStatus, CacheConfig, CacheConfigRequest, CacheTestResult, SentryConfig, SentryConfigRequest, SentryTestResult, UserListResponse, UserStatsResponse, CreateUserRequest, UpdateUserRequest, ResetPasswordRequest } from '../types'
import type { UserNotificationConfig, AdminSMTPConfig } from '../types/workflow'
import type { EnabledAgentOption } from '../types/swarm'
import { fetchAPI, setTokens, getRefreshToken, clearTokens } from './core'

// Authentication
export async function login(data: LoginRequest): Promise<AuthResponse> {
  const response = await fetchAPI<AuthResponse>('/auth/login', {
    method: 'POST',
    body: JSON.stringify(data),
    skipAuth: true,
  })
  setTokens(response.tokens.access_token, response.tokens.refresh_token)
  return response
}

export async function register(data: RegisterRequest): Promise<AuthResponse> {
  const response = await fetchAPI<AuthResponse>('/auth/register', {
    method: 'POST',
    body: JSON.stringify(data),
    skipAuth: true,
  })
  setTokens(response.tokens.access_token, response.tokens.refresh_token)
  return response
}

export async function logout(): Promise<void> {
  const refreshToken = getRefreshToken()
  if (refreshToken) {
    try {
      await fetchAPI('/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refresh_token: refreshToken }),
      })
    } catch {
      // Ignore errors during logout
    }
  }
  clearTokens()
}

export async function logoutAll(): Promise<void> {
  try {
    await fetchAPI('/auth/logout-all', {
      method: 'POST',
    })
  } catch {
    // Ignore errors
  }
  clearTokens()
}

export async function getCurrentUser(): Promise<User> {
  return fetchAPI('/auth/me')
}

export async function refreshTokens(refreshToken: string): Promise<TokenResponse> {
  const response = await fetchAPI<TokenResponse>('/auth/refresh', {
    method: 'POST',
    body: JSON.stringify({ refresh_token: refreshToken }),
    skipAuth: true,
  })
  setTokens(response.access_token, response.refresh_token)
  return response
}

// OAuth
export async function getOAuthProviders(): Promise<{ providers: OAuthProvider[]; count: number }> {
  return fetchAPI('/auth/oauth/providers', { skipAuth: true })
}

// Sign-in starts unauthenticated. With { link: true } the signed-in user's
// token is sent so the server connects the provider to this account instead.
export async function initiateOAuth(provider: string, options?: { link?: boolean }): Promise<{ auth_url: string; provider: string }> {
  if (options?.link) {
    return fetchAPI(`/auth/oauth/${provider}`, { method: 'POST', body: JSON.stringify({ link: true }) })
  }
  return fetchAPI(`/auth/oauth/${provider}`, { method: 'POST', skipAuth: true })
}

// Sign-in answers with tokens; a connect flow answers { linked: true, provider }.
export type OAuthExchangeResult =
  | { access_token: string; refresh_token: string; expires_in: number; linked?: undefined }
  | { linked: true; provider: string }

export async function exchangeOAuthCode(code: string, state: string, provider: string, inviteCode?: string): Promise<OAuthExchangeResult> {
  const body: Record<string, string> = { code, state, provider }
  if (inviteCode) body.invite_code = inviteCode
  return fetchAPI('/auth/oauth/exchange', {
    method: 'POST',
    body: JSON.stringify(body),
    skipAuth: true,
  })
}

export interface ConnectedAccount {
  provider: string
  email: string
  created_at: string
}

// Sign-in providers linked to the signed-in user (tokens are never returned).
export async function getConnectedAccounts(): Promise<{ accounts: ConnectedAccount[]; count: number }> {
  return fetchAPI('/auth/oauth/accounts')
}

// Password Change
export async function changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
  return fetchAPI('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  })
}

// Profile Update
export async function updateProfile(name: string): Promise<User> {
  return fetchAPI<User>('/auth/me', {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  })
}

// User Preferences
export async function getUserPreferences(): Promise<Record<string, string>> {
  return fetchAPI<Record<string, string>>('/user/preferences')
}

export async function updateUserPreferences(prefs: Record<string, string>): Promise<void> {
  await fetchAPI('/user/preferences', {
    method: 'PUT',
    body: JSON.stringify(prefs),
  })
}

// Organizations
export async function listOrganizations(): Promise<{ organizations: Organization[]; count: number }> {
  return fetchAPI('/orgs')
}

export async function createOrganization(data: CreateOrgRequest): Promise<Organization> {
  return fetchAPI('/orgs', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function getOrganization(id: string): Promise<Organization> {
  return fetchAPI(`/orgs/${id}`)
}

export async function updateOrganization(id: string, data: UpdateOrgRequest): Promise<Organization> {
  return fetchAPI(`/orgs/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

export async function deleteOrganization(id: string): Promise<{ message: string }> {
  return fetchAPI(`/orgs/${id}`, {
    method: 'DELETE',
  })
}

// Organization Members
export async function listOrgMembers(orgId: string): Promise<{ members: OrgMembership[]; count: number }> {
  return fetchAPI(`/orgs/${orgId}/members`)
}

export async function addOrgMember(orgId: string, data: AddMemberRequest): Promise<OrgMembership> {
  return fetchAPI(`/orgs/${orgId}/members`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function updateOrgMemberRole(orgId: string, userId: string, data: UpdateMemberRoleRequest): Promise<{ message: string }> {
  return fetchAPI(`/orgs/${orgId}/members/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

export async function removeOrgMember(orgId: string, userId: string): Promise<{ message: string }> {
  return fetchAPI(`/orgs/${orgId}/members/${userId}`, {
    method: 'DELETE',
  })
}

// Organization Invites
export async function createOrgInvite(orgId: string, data: CreateInviteRequest): Promise<{ invite: OrgInvite; invite_url: string }> {
  return fetchAPI(`/orgs/${orgId}/invites`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function listOrgInvites(orgId: string): Promise<{ invites: OrgInvite[]; count: number }> {
  return fetchAPI(`/orgs/${orgId}/invites`)
}

export async function cancelOrgInvite(orgId: string, inviteId: string): Promise<{ message: string }> {
  return fetchAPI(`/orgs/${orgId}/invites/${inviteId}`, {
    method: 'DELETE',
  })
}

export async function acceptOrgInvite(token: string): Promise<{ message: string; membership: OrgMembership }> {
  return fetchAPI(`/orgs/invites/${token}`, {
    method: 'POST',
  })
}

// Teams
export async function listTeams(orgId: string): Promise<{ teams: Team[]; count: number }> {
  return fetchAPI(`/orgs/${orgId}/teams`)
}

export async function createTeam(orgId: string, data: CreateTeamRequest): Promise<Team> {
  return fetchAPI(`/orgs/${orgId}/teams`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function getTeam(orgId: string, teamId: string): Promise<Team> {
  return fetchAPI(`/orgs/${orgId}/teams/${teamId}`)
}

export async function updateTeam(orgId: string, teamId: string, data: UpdateTeamRequest): Promise<Team> {
  return fetchAPI(`/orgs/${orgId}/teams/${teamId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

export async function deleteTeam(orgId: string, teamId: string): Promise<{ message: string }> {
  return fetchAPI(`/orgs/${orgId}/teams/${teamId}`, {
    method: 'DELETE',
  })
}

// Team Members
export async function listTeamMembers(orgId: string, teamId: string): Promise<{ members: TeamMembership[]; count: number }> {
  return fetchAPI(`/orgs/${orgId}/teams/${teamId}/members`)
}

export async function addTeamMember(orgId: string, teamId: string, data: AddMemberRequest): Promise<TeamMembership> {
  return fetchAPI(`/orgs/${orgId}/teams/${teamId}/members`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function removeTeamMember(orgId: string, teamId: string, userId: string): Promise<{ message: string }> {
  return fetchAPI(`/orgs/${orgId}/teams/${teamId}/members/${userId}`, {
    method: 'DELETE',
  })
}

// Billing - Plans
export async function listPlans(): Promise<{ plans: Plan[]; count: number }> {
  return fetchAPI('/billing/plans')
}

export async function getPlan(id: string): Promise<Plan> {
  return fetchAPI(`/billing/plans/${id}`)
}

// Billing - Subscription (organization-scoped)
export async function getSubscription(orgId: string): Promise<SubscriptionResponse> {
  return fetchAPI(`/orgs/${orgId}/billing/subscription`)
}

export async function createCheckoutSession(orgId: string, data: CreateSubscriptionRequest): Promise<CheckoutSession> {
  return fetchAPI(`/orgs/${orgId}/billing/checkout`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function confirmCheckout(orgId: string, sessionId: string): Promise<{ message: string }> {
  return fetchAPI(`/orgs/${orgId}/billing/checkout/confirm`, {
    method: 'POST',
    body: JSON.stringify({ session_id: sessionId }),
  })
}

export async function createBillingPortalSession(orgId: string): Promise<BillingPortalSession> {
  return fetchAPI(`/orgs/${orgId}/billing/portal`, {
    method: 'POST',
  })
}

export async function cancelSubscription(orgId: string, data: CancelSubscriptionRequest): Promise<{ message: string }> {
  return fetchAPI(`/orgs/${orgId}/billing/subscription/cancel`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function reactivateSubscription(orgId: string): Promise<{ message: string }> {
  return fetchAPI(`/orgs/${orgId}/billing/subscription/reactivate`, {
    method: 'POST',
  })
}

// Billing - Invoices
export async function listInvoices(orgId: string): Promise<{ invoices: Invoice[]; count: number }> {
  return fetchAPI(`/orgs/${orgId}/billing/invoices`)
}

export async function getInvoice(orgId: string, invoiceId: string): Promise<Invoice> {
  return fetchAPI(`/orgs/${orgId}/billing/invoices/${invoiceId}`)
}

// Billing - Payment Methods
export async function listPaymentMethods(orgId: string): Promise<{ payment_methods: PaymentMethod[]; count: number }> {
  return fetchAPI(`/orgs/${orgId}/billing/payment-methods`)
}

export async function setDefaultPaymentMethod(orgId: string, paymentMethodId: string): Promise<{ message: string }> {
  return fetchAPI(`/orgs/${orgId}/billing/payment-methods/${paymentMethodId}/default`, {
    method: 'POST',
  })
}

export async function deletePaymentMethod(orgId: string, paymentMethodId: string): Promise<{ message: string }> {
  return fetchAPI(`/orgs/${orgId}/billing/payment-methods/${paymentMethodId}`, {
    method: 'DELETE',
  })
}

// Billing - Usage
export async function getUsage(orgId: string): Promise<{ plan: Plan; usage_status: UsageSummary }> {
  return fetchAPI(`/orgs/${orgId}/billing/usage`)
}

export async function getUsageHistory(orgId: string, periods?: number): Promise<{ history: UsageSummary[]; count: number }> {
  const params = periods ? `?periods=${periods}` : ''
  return fetchAPI(`/orgs/${orgId}/billing/usage/history${params}`)
}

// Billing - Admin Configuration
export async function getBillingConfig(): Promise<{ enabled: boolean }> {
  return fetchAPI('/admin/billing/config')
}

export async function updateBillingConfig(enabled: boolean): Promise<{ message: string; enabled: boolean }> {
  return fetchAPI('/admin/billing/config', {
    method: 'PUT',
    body: JSON.stringify({ enabled }),
  })
}

export async function listBillingExemptions(): Promise<{ exemptions: BillingExemption[]; count: number }> {
  return fetchAPI('/admin/billing/exemptions')
}

export async function createBillingExemption(userId: string, reason?: string): Promise<{ message: string }> {
  return fetchAPI('/admin/billing/exemptions', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, reason }),
  })
}

export async function deleteBillingExemption(userId: string): Promise<{ message: string }> {
  return fetchAPI(`/admin/billing/exemptions/${userId}`, {
    method: 'DELETE',
  })
}

// Stripe Configuration (Admin)
export interface StripeConfigStatus {
  connected: boolean
  secret_key_configured: boolean
  publishable_key_configured: boolean
  webhook_secret_configured: boolean
  success_url: string
  cancel_url: string
  portal_return_url: string
}

export async function getStripeConfig(): Promise<StripeConfigStatus> {
  return fetchAPI('/admin/billing/stripe')
}

export async function updateStripeConfig(config: {
  secret_key?: string
  publishable_key?: string
  webhook_secret?: string
  success_url?: string
  cancel_url?: string
  portal_return_url?: string
}): Promise<{ message: string }> {
  return fetchAPI('/admin/billing/stripe', {
    method: 'PUT',
    body: JSON.stringify(config),
  })
}

export async function deleteStripeConfig(): Promise<{ message: string }> {
  return fetchAPI('/admin/billing/stripe', {
    method: 'DELETE',
  })
}

// Admin Plan Stripe Mapping
export interface AdminPlan {
  id: string
  name: string
  slug: string
  price_monthly: number
  price_yearly: number
  stripe_price_id: string
  stripe_yearly_id: string
  is_public: boolean
}

export async function getAdminPlans(): Promise<{ plans: AdminPlan[] }> {
  return fetchAPI('/admin/billing/plans')
}

export async function updatePlanStripeIds(planId: string, stripePriceId: string, stripeYearlyId: string): Promise<{ message: string }> {
  return fetchAPI(`/admin/billing/plans/${planId}/stripe`, {
    method: 'PUT',
    body: JSON.stringify({ stripe_price_id: stripePriceId, stripe_yearly_id: stripeYearlyId }),
  })
}

export async function syncPlansToStripe(): Promise<{ message: string; synced: number }> {
  return fetchAPI('/admin/billing/sync-stripe', {
    method: 'POST',
  })
}

// Billing - Subscription CRUD (organization-scoped)
export async function createSubscription(orgId: string, data: { plan_id: string; billing_cycle: string }): Promise<SubscriptionResponse> {
  return fetchAPI(`/orgs/${orgId}/billing/subscription`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function changePlan(orgId: string, data: { plan_id: string }): Promise<{ message: string }> {
  return fetchAPI(`/orgs/${orgId}/billing/subscription`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

export async function deleteSubscription(orgId: string, data?: { cancel_at_period_end?: boolean }): Promise<{ message: string }> {
  return fetchAPI(`/orgs/${orgId}/billing/subscription`, {
    method: 'DELETE',
    body: data ? JSON.stringify(data) : undefined,
  })
}

// API Keys
export async function listAPIKeys(): Promise<{ api_keys: APIKey[]; count: number }> {
  return fetchAPI('/api-keys')
}

export async function createAPIKey(data: CreateAPIKeyRequest): Promise<CreateAPIKeyResponse> {
  return fetchAPI('/api-keys', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function getAPIKey(id: string): Promise<APIKey> {
  return fetchAPI(`/api-keys/${id}`)
}

export async function revokeAPIKey(id: string): Promise<{ message: string; id: string }> {
  return fetchAPI(`/api-keys/${id}`, {
    method: 'DELETE',
  })
}

export async function getAvailableScopes(): Promise<{ scopes: APIKeyScope[]; count: number }> {
  return fetchAPI('/api-keys/scopes')
}

// Audit Logs
export async function listAuditLogs(filter?: AuditLogFilter): Promise<{ logs: AuditLog[]; total: number; limit: number; offset: number }> {
  const params = new URLSearchParams()
  if (filter?.user_id) params.set('user_id', filter.user_id)
  if (filter?.org_id) params.set('org_id', filter.org_id)
  if (filter?.action) params.set('action', filter.action)
  if (filter?.resource_type) params.set('resource_type', filter.resource_type)
  if (filter?.resource_id) params.set('resource_id', filter.resource_id)
  if (filter?.status) params.set('status', filter.status)
  if (filter?.ip_address) params.set('ip_address', filter.ip_address)
  if (filter?.start_time) params.set('start_time', filter.start_time)
  if (filter?.end_time) params.set('end_time', filter.end_time)
  if (filter?.limit) params.set('limit', filter.limit.toString())
  if (filter?.offset) params.set('offset', filter.offset.toString())

  const query = params.toString()
  return fetchAPI(`/audit${query ? `?${query}` : ''}`)
}

export async function getAuditLog(id: string): Promise<AuditLog> {
  return fetchAPI(`/audit/${id}`)
}

export async function getAuditEventTypes(): Promise<{ event_types: AuditEventType[]; count: number }> {
  return fetchAPI('/audit/event-types')
}

export async function listOrgAuditLogs(orgId: string, filter?: AuditLogFilter): Promise<{ logs: AuditLog[]; total: number; limit: number; offset: number }> {
  const params = new URLSearchParams()
  if (filter?.user_id) params.set('user_id', filter.user_id)
  if (filter?.action) params.set('action', filter.action)
  if (filter?.resource_type) params.set('resource_type', filter.resource_type)
  if (filter?.status) params.set('status', filter.status)
  if (filter?.start_time) params.set('start_time', filter.start_time)
  if (filter?.end_time) params.set('end_time', filter.end_time)
  if (filter?.limit) params.set('limit', filter.limit.toString())
  if (filter?.offset) params.set('offset', filter.offset.toString())

  const query = params.toString()
  return fetchAPI(`/orgs/${orgId}/audit${query ? `?${query}` : ''}`)
}

// Feedback
export interface FeedbackSubmission {
  id: string
  user_id: string
  user_email: string
  user_name: string
  type: 'feedback' | 'issue'
  subject: string
  message: string
  status: 'new' | 'reviewed' | 'resolved' | 'archived'
  created_at: string
  updated_at: string
}

export async function submitFeedback(data: { type: 'feedback' | 'issue'; subject: string; message: string }): Promise<{ id: string; message: string }> {
  return fetchAPI('/feedback', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function listFeedback(params?: { type?: string; status?: string; limit?: number; offset?: number }): Promise<{ items: FeedbackSubmission[]; total: number }> {
  const searchParams = new URLSearchParams()
  if (params?.type) searchParams.set('type', params.type)
  if (params?.status) searchParams.set('status', params.status)
  if (params?.limit) searchParams.set('limit', String(params.limit))
  if (params?.offset) searchParams.set('offset', String(params.offset))
  const qs = searchParams.toString()
  return fetchAPI(`/admin/feedback${qs ? '?' + qs : ''}`)
}

export async function getFeedbackItem(id: string): Promise<FeedbackSubmission> {
  return fetchAPI(`/admin/feedback/${id}`)
}

export async function updateFeedbackStatus(id: string, status: string): Promise<{ message: string }> {
  return fetchAPI(`/admin/feedback/${id}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  })
}

// Admin User Management
export async function listUsers(limit: number = 20, offset: number = 0): Promise<UserListResponse> {
  return fetchAPI(`/admin/users?limit=${limit}&offset=${offset}`)
}

export async function getUserStats(): Promise<UserStatsResponse> {
  return fetchAPI('/admin/users/stats')
}

export async function getUser(userId: string): Promise<User> {
  return fetchAPI(`/admin/users/${userId}`)
}

export async function createUser(data: CreateUserRequest): Promise<User> {
  return fetchAPI('/admin/users', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function updateUser(userId: string, data: UpdateUserRequest): Promise<User> {
  return fetchAPI(`/admin/users/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

export async function deleteUser(userId: string): Promise<{ message: string }> {
  return fetchAPI(`/admin/users/${userId}`, {
    method: 'DELETE',
  })
}

export async function resetUserPassword(userId: string, data: ResetPasswordRequest): Promise<{ message: string }> {
  return fetchAPI(`/admin/users/${userId}/reset-password`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

// Admin Storage API
export async function getStorageConfig(): Promise<StorageConfig> {
  return fetchAPI('/admin/storage/config')
}

export async function updateStorageConfig(data: StorageConfigRequest): Promise<{ message: string; restart_required: boolean; note: string }> {
  return fetchAPI('/admin/storage/config', {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

export async function testStorageConnection(data: StorageConfigRequest): Promise<StorageTestResult> {
  return fetchAPI('/admin/storage/test', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function resetStorageConfig(): Promise<{ message: string; restart_required: boolean; note: string }> {
  return fetchAPI('/admin/storage/config', {
    method: 'DELETE',
  })
}

export async function getStorageStatus(): Promise<StorageStatus> {
  return fetchAPI('/admin/storage/status')
}

// Admin Cache API
export async function getCacheConfig(): Promise<CacheConfig> {
  return fetchAPI('/admin/cache/config')
}

export async function updateCacheConfig(data: CacheConfigRequest): Promise<{ message: string; restart_required: boolean; note: string }> {
  return fetchAPI('/admin/cache/config', {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

export async function testCacheConnection(data: CacheConfigRequest): Promise<CacheTestResult> {
  return fetchAPI('/admin/cache/test', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function resetCacheConfig(): Promise<{ message: string; restart_required: boolean; note: string }> {
  return fetchAPI('/admin/cache/config', {
    method: 'DELETE',
  })
}

// Admin Sentry API
export async function getSentryConfig(): Promise<SentryConfig> {
  return fetchAPI('/admin/sentry/config')
}

export async function updateSentryConfig(data: SentryConfigRequest): Promise<{ message: string; restart_required: boolean; note: string }> {
  return fetchAPI('/admin/sentry/config', {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

export async function testSentryConnection(data: SentryConfigRequest): Promise<SentryTestResult> {
  return fetchAPI('/admin/sentry/test', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function resetSentryConfig(): Promise<{ message: string; restart_required: boolean; note: string }> {
  return fetchAPI('/admin/sentry/config', {
    method: 'DELETE',
  })
}

// Admin AI API
export interface AIProviderStatus {
  name: string
  tier: number
  available: boolean
  model?: string
}

// CapabilityRow maps a product capability to the provider/model that serves it.
export interface CapabilityRow {
  capability: string
  label: string
  provider: string
  model?: string
  models?: string[]
  note?: string
}

export interface AIModelInfo {
  id: string
  display_name: string
  free?: boolean
}

export interface OAuthTokenStatus {
  configured: boolean
  expires_at?: string
  status: 'ok' | 'expiring_soon' | 'expired' | 'no_expiry_info' | 'not_configured'
}

export interface AIStatusResponse {
  available: boolean
  providers: AIProviderStatus[]
  available_models: Record<string, AIModelInfo[]>
  config?: {
    ActiveProvider: string
    FallbackEnabled: boolean
    FallbackOrder: string
  }
  keys: {
    anthropic_configured: boolean
    openrouter_configured: boolean
    claude_code_configured: boolean
    claude_code_oauth_configured: boolean
    do_inference_configured: boolean
    gateway_pull_token_configured: boolean
  }
  oauth_token_status?: OAuthTokenStatus
  capability_matrix?: CapabilityRow[]
}

export async function getAIStatus(): Promise<AIStatusResponse> {
  return fetchAPI<AIStatusResponse>('/admin/ai/status')
}

export async function refreshClaudeCodeToken(): Promise<{ message: string; token_status: OAuthTokenStatus }> {
  return fetchAPI<{ message: string; token_status: OAuthTokenStatus }>('/admin/ai/refresh-token', {
    method: 'POST',
  })
}

export interface AITestResult {
  ok: boolean
  provider: string
  model?: string
  latency_ms: number
  error?: string
}

// testAIProvider runs a live connection test against a provider (anthropic,
// openrouter, do_inference, claude_code). Optional apiKey/model override the
// stored settings so a candidate key can be tested before saving. Admin only.
export async function testAIProvider(provider: string, apiKey?: string, model?: string): Promise<AITestResult> {
  return fetchAPI<AITestResult>('/admin/ai/test', {
    method: 'POST',
    body: JSON.stringify({
      provider,
      ...(apiKey ? { api_key: apiKey } : {}),
      ...(model ? { model } : {}),
    }),
  })
}

// ModelOption is one admin-defined model in the per-task picker, with its tier
// and whether the requesting user's billing plan can select it.
export interface ModelOption {
  id: string
  tier: string // "free" | "paid" | "pro"
  locked: boolean
  provider?: string // admin catalog binding: anthropic | openrouter | do_inference
  available?: boolean // false when the bound provider has no API key configured
}

export interface AIModelsResponse {
  models: ModelOption[]
  default: string
}

// getAIModels returns the admin-defined models for the per-task picker, each
// marked locked when the user's plan can't select that tier, plus the default.
// Authenticated (not admin). Pass the current org so locking reflects its plan.
export async function getAIModels(orgId?: string): Promise<AIModelsResponse> {
  const q = orgId ? `?org_id=${encodeURIComponent(orgId)}` : ''
  return fetchAPI<AIModelsResponse>(`/ai/models${q}`)
}

// getEnabledAgents returns the enabled coding agents (minimal, no credentials)
// for the comparison variant picker. Authenticated (not admin).
export async function getEnabledAgents(): Promise<{ agents: EnabledAgentOption[] }> {
  return fetchAPI<{ agents: EnabledAgentOption[] }>(`/ai/agents`)
}

export interface CodingAgent {
  id: string
  name: string
  type: string
  api_url?: string
  enabled: boolean
  is_default: boolean
  config?: string
  has_auth: boolean
  created_at: string
  updated_at: string
}

export interface CodingAgentRequest {
  name: string
  type: string
  api_url?: string
  enabled?: boolean
  config?: string
  auth?: string
}

export async function listCodingAgents(): Promise<{ agents: CodingAgent[] }> {
  return fetchAPI<{ agents: CodingAgent[] }>('/admin/ai/agents')
}

export async function createCodingAgent(req: CodingAgentRequest): Promise<CodingAgent> {
  return fetchAPI<CodingAgent>('/admin/ai/agents', { method: 'POST', body: JSON.stringify(req) })
}

export async function updateCodingAgent(id: string, req: CodingAgentRequest): Promise<CodingAgent> {
  return fetchAPI<CodingAgent>(`/admin/ai/agents/${id}`, { method: 'PUT', body: JSON.stringify(req) })
}

export async function deleteCodingAgent(id: string): Promise<{ message: string }> {
  return fetchAPI<{ message: string }>(`/admin/ai/agents/${id}`, { method: 'DELETE' })
}

export async function setDefaultCodingAgent(id: string): Promise<{ message: string }> {
  return fetchAPI<{ message: string }>(`/admin/ai/agents/${id}/default`, { method: 'POST' })
}

// Preview Mode API
export async function getPreviewStatus(): Promise<{ enabled: boolean }> {
  return fetchAPI<{ enabled: boolean }>('/auth/preview-status', { skipAuth: true })
}

export async function getPreviewConfig(): Promise<{ enabled: boolean; code: string }> {
  return fetchAPI<{ enabled: boolean; code: string }>('/admin/preview/config')
}

export async function updatePreviewConfig(data: { enabled: boolean; code?: string }): Promise<{ message: string }> {
  return fetchAPI<{ message: string }>('/admin/preview/config', {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

// Email Verification API
export async function verifyEmail(token: string): Promise<{ message: string; user: User; access_token?: string; refresh_token?: string }> {
  return fetchAPI<{ message: string; user: User; access_token?: string; refresh_token?: string }>('/auth/verify-email', {
    method: 'POST',
    body: JSON.stringify({ token }),
    skipAuth: true,
  })
}

export async function resendVerification(): Promise<{ message: string }> {
  return fetchAPI<{ message: string }>('/auth/resend-verification', {
    method: 'POST',
  })
}

export async function getVerificationStatus(): Promise<{ required: boolean }> {
  return fetchAPI<{ required: boolean }>('/auth/verification-status', { skipAuth: true })
}

export async function getEmailVerificationConfig(): Promise<{ enabled: boolean }> {
  return fetchAPI<{ enabled: boolean }>('/admin/email-verification/config')
}

export async function updateEmailVerificationConfig(data: { enabled: boolean }): Promise<{ message: string }> {
  return fetchAPI<{ message: string }>('/admin/email-verification/config', {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

// User Notification Config
export async function getUserNotificationConfig(): Promise<UserNotificationConfig> {
  return fetchAPI<UserNotificationConfig>('/notifications/config')
}

export async function updateUserNotificationConfig(config: Partial<UserNotificationConfig>): Promise<{ message: string }> {
  return fetchAPI<{ message: string }>('/notifications/config', {
    method: 'PUT',
    body: JSON.stringify(config),
  })
}

// Org Notification Config
export async function getOrgNotificationConfig(orgId: string): Promise<UserNotificationConfig> {
  return fetchAPI<UserNotificationConfig>(`/orgs/${orgId}/notifications/config`)
}

export async function updateOrgNotificationConfig(orgId: string, config: Partial<UserNotificationConfig>): Promise<{ message: string }> {
  return fetchAPI<{ message: string }>(`/orgs/${orgId}/notifications/config`, {
    method: 'PUT',
    body: JSON.stringify(config),
  })
}

// Org Secrets
export interface OrgSecret {
  id: string
  org_id: string
  name: string
  created_by: string
  created_at: string
  updated_at: string
}

export interface ListSecretsResponse {
  secrets: OrgSecret[]
  total: number
}

export async function listOrgSecrets(orgId: string): Promise<ListSecretsResponse> {
  return fetchAPI<ListSecretsResponse>(`/orgs/${orgId}/secrets`)
}

export async function createOrgSecret(orgId: string, name: string, value: string): Promise<OrgSecret> {
  return fetchAPI<OrgSecret>(`/orgs/${orgId}/secrets`, {
    method: 'POST',
    body: JSON.stringify({ name, value }),
  })
}

export async function deleteOrgSecret(orgId: string, name: string): Promise<void> {
  await fetchAPI(`/orgs/${orgId}/secrets/${encodeURIComponent(name)}`, {
    method: 'DELETE',
  })
}

// Admin SMTP Config
export async function getAdminSMTPConfig(): Promise<AdminSMTPConfig> {
  return fetchAPI<AdminSMTPConfig>('/admin/notifications/config')
}

export async function updateAdminSMTPConfig(config: Partial<AdminSMTPConfig>): Promise<{ message: string }> {
  return fetchAPI<{ message: string }>('/admin/notifications/config', {
    method: 'PUT',
    body: JSON.stringify(config),
  })
}

export async function testAdminSMTP(): Promise<{ success: boolean; message?: string; error?: string }> {
  return fetchAPI<{ success: boolean; message?: string; error?: string }>('/admin/notifications/test-smtp', {
    method: 'POST',
  })
}

export async function testAdminResend(): Promise<{ success: boolean; message?: string; error?: string }> {
  return fetchAPI<{ success: boolean; message?: string; error?: string }>('/admin/notifications/test-resend', {
    method: 'POST',
  })
}
